import { PCFShadowMap, SRGBColorSpace, WebGLRenderer, type WebGLRenderTarget } from '@seddi/three';
import log from 'loglevel';

export function createRendererContextAttributes() {
  const contextAttributes: WebGLContextAttributes = {
    alpha: false,
    antialias: true,
    preserveDrawingBuffer: false,
    stencil: false,
  };
  return contextAttributes;
}

export function createRenderer(
  canvas: HTMLCanvasElement,
  contextAttributes: WebGLContextAttributes,
): WebGLRenderer {
  // create GPU renderer
  const renderer = new WebGLRenderer({
    alpha: contextAttributes.alpha,
    canvas,
    context: canvas.getContext('webgl2', contextAttributes),
  });

  // configure GPU renderer
  renderer.autoClear = false;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFShadowMap;

  // AVOID DEBUG
  renderer.debug.checkShaderErrors = false;

  return renderer;
}

function fp16ToUint8(h: number): number {
  const exp = (h >> 10) & 0x1f;
  const man = h & 0x3ff;
  if (exp === 0) return 0;
  if (exp >= 31 || exp > 15) return 255;
  return ((1024 + man) * 255) >> (25 - exp);
}

export async function readRenderTargetPixelsFloat16(
  self: WebGLRenderer,
  renderTarget: WebGLRenderTarget,
): Promise<Uint8ClampedArray> {
  const t0 = performance.now();
  const { height, width } = renderTarget;
  const size = width * height * 4;
  const bufferU16 = new Uint16Array(size);
  await self.readRenderTargetPixelsAsync(renderTarget, 0, 0, width, height, bufferU16);
  const pixels = new Uint8ClampedArray(size);
  for (let i = 0; i < size; ++i) {
    pixels[i] = fp16ToUint8(bufferU16[i]);
  }
  const t1 = performance.now();
  log.info(`readRenderTargetPixelsFloat16 took ${t1 - t0} ms`);
  return pixels;
}

export function setRendererSize(
  self: WebGLRenderer,
  width: number,
  height: number,
  devicePixelRatio = 1,
) {
  // NOTE: use setDrawingBufferSize when sceneRendererOIT is out of its implementation
  self.setPixelRatio(devicePixelRatio);
  self.setSize(width, height, false);
}

/**
 * Sets tone mapping exposure.
 * @param self - Scene.
 * @param exposure - Exposure in logarithmic scale.
 */
export function setToneMappingExposure(self: WebGLRenderer, exposure: number) {
  self.toneMappingExposure = Math.pow(2, exposure);
}
